import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Divider,
    Paper,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SCUILoadingLinear, SCUIDateRangerV2 } from '@/atomic/atoms';
import GlobalSmsRateLimitQuery from '@/services/query/globalSmsRateLimit/GlobalSmsRateLimitQuery.services';
import TimeHelper from '@/utils/time';
import { format, subDays } from 'date-fns';
import { MSM } from '../../membershipSmsManagement.classes';

export const UpdateHistory = ({
    selectedCountry,
    selectedPlatform,
}: {
    selectedCountry: any;
    selectedPlatform: number;
}) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const sevenDaysAgo = format(subDays(new Date(), 7), 'yyyy-MM-dd');

    const [limitHistory, setLimitHistory] = useState<any>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [expanded, setExpanded] = useState<string | false>(false);
    const [startDate, setStartDate] = useState<string>(sevenDaysAgo);
    const [endDate, setEndDate] = useState<string>(today);
    
    // Track date selection: false = waiting for startDate, true = waiting for endDate
    const isSelectingEndDate = useRef<boolean>(false);

    const { t } = useTranslation();

    const getCountryLimitHistory = () => {
        setIsLoading(true);
        GlobalSmsRateLimitQuery.GetCountryLimitHistory({
            CountryCode: selectedCountry?.countryCode,
            Platform: selectedPlatform,
            StartDate: startDate,
            EndDate: endDate,
        })
            .then((res: any) => {
                if (res?.data) {
                    const dataWithId = (res.data || []).map((item: any, index: number) => ({
                        ...item,
                        id: index,
                    }));
                    setLimitHistory(dataWithId);
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (selectedCountry && selectedPlatform) {
            getCountryLimitHistory();
        }
    }, [selectedCountry, selectedPlatform]);

    const handleDateChange = (event: any) => {
        const newStartDate = event[0];
        const newEndDate = event[1];

        setStartDate(newStartDate);
        setEndDate(newEndDate);

        // First click = startDate selection, second click = endDate selection
        if (isSelectingEndDate.current) {
            // Second click: user selected endDate, make API call
            isSelectingEndDate.current = false;
            
            if (newStartDate && newEndDate && selectedCountry && selectedPlatform) {
                setIsLoading(true);
                GlobalSmsRateLimitQuery.GetCountryLimitHistory({
                    CountryCode: selectedCountry?.countryCode,
                    Platform: selectedPlatform,
                    StartDate: newStartDate,
                    EndDate: newEndDate,
                })
                    .then((res: any) => {
                        if (res?.data) {
                            const dataWithId = (res.data || []).map((item: any, index: number) => ({
                                ...item,
                                id: index,
                            }));
                            setLimitHistory(dataWithId);
                        }
                    })
                    .catch(() => {
                        Swal.fire(t('general.anErrorOccured'), '', 'error');
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            }
        } else {
            // First click: user selected startDate, wait for endDate
            isSelectingEndDate.current = true;
        }
    };

    const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    return (
        <Box className={MSM.updateHistoryTabRoot} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <SCUILoadingLinear display={isLoading} />
            <Box className={MSM.updateHistoryToolbar} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>
                    {t('membershipSmsManagement.updateHistory')}
                </Typography>
                <Box className={MSM.updateHistoryDateRangeWrap} sx={{ width: '300px' }}>
                    <SCUIDateRangerV2
                        fromDate={startDate}
                        toDate={endDate}
                        setDateRange={handleDateChange}
                        dateFormat="YYYY-MM-DD"
                        fullWidth
                    />
                </Box>
            </Box>
            <TableContainer className={MSM.updateHistoryTableWrap} sx={{ width: '100%', boxShadow: 3 }} component={Paper}>
                <Table>
                    <TableHead sx={{ backgroundColor: '#e8e8e8' }}>
                        <TableRow>
                            <TableCell
                                sx={{ fontWeight: 'bold', fontSize: '14px', minWidth: '25%', width: '25%', p: 2 }}
                            >
                                {t('membershipSmsManagement.country')}
                            </TableCell>
                            <TableCell
                                sx={{ fontWeight: 'bold', fontSize: '14px', minWidth: '25%', width: '25%', p: 2 }}
                            >
                                {t('membershipSmsManagement.endpoint')}
                            </TableCell>
                            <TableCell
                                sx={{ fontWeight: 'bold', fontSize: '14px', minWidth: '25%', width: '25%', p: 2 }}
                            >
                                {t('membershipSmsManagement.createdDate')}
                            </TableCell>
                            <TableCell
                                sx={{ fontWeight: 'bold', fontSize: '14px', minWidth: '25%', width: '25%', p: 2 }}
                            >
                                {t('membershipSmsManagement.userName')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                </Table>
                <Box>
                    {(limitHistory || []).map((item: any, index: any) => (
                        <Accordion
                            className={MSM.updateHistoryAccordion}
                            key={item.id}
                            expanded={expanded === `panel-${index}`}
                            onChange={handleAccordionChange(`panel-${index}`)}
                            disableGutters
                            elevation={0}
                            sx={{
                                '&:before': {
                                    display: 'none',
                                },
                                borderTop: index === 0 ? 'none' : '1px solid rgba(224, 224, 224, 1)',
                                '&:not(:last-child)': {
                                    borderBottom: 0,
                                },
                                '&.Mui-expanded': {
                                    margin: 0,
                                    backgroundColor: '#f0f0f0',
                                },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`panel-${index}-content`}
                                id={`panel-${index}`}
                                sx={{
                                    minHeight: '48px',
                                    '&.Mui-expanded': {
                                        minHeight: '48px',
                                    },
                                    '& .MuiAccordionSummary-content': {
                                        margin: 0,
                                        '&.Mui-expanded': {
                                            margin: 0,
                                        },
                                    },
                                    px: 2,
                                    py: 1.5,
                                }}
                            >
                                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                                    <Box sx={{ width: '25%' }}>
                                        <Typography sx={{ fontSize: '14px' }}>
                                            {selectedCountry?.translatedCountryName} | {selectedCountry?.countryId}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ width: '25%' }}>
                                        <Typography sx={{ fontSize: '14px', paddingLeft: '8px' }}>
                                            {item.endpoint ? item.endpoint : '-'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ width: '25%' }}>
                                        <Typography sx={{ fontSize: '14px', paddingLeft: '12px' }}>
                                            {item.createDate ? TimeHelper.formatDateHour(item.createDate) : '-'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ width: '25%' }}>
                                        <Typography sx={{ fontSize: '14px', paddingLeft: '24px' }}>
                                            {item.userName || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails
                                className={MSM.updateHistoryAccordionDetails}
                                sx={{
                                    p: 2,
                                    border: '1px solid #e0e0e0',
                                    borderTop: '1px solid rgba(224, 224, 224, 1)',
                                    backgroundColor: '#f7f7f7',
                                }}
                            >
                                {/* Grid layout for hour:limit values - 6 columns x 4 rows */}
                                <Box
                                    className={MSM.updateHistoryHourGrid}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(6, 1fr)',
                                        gap: '12px',
                                        width: '100%',
                                    }}
                                >
                                    {Array.from({ length: 24 }).map((_, hourIdx) => {
                                        const hourString = String(hourIdx).padStart(2, '0');
                                        const hourData = (item.hourLimits || []).find((v: any) => v.hour === hourIdx);
                                        const limit = hourData?.limit || 0;

                                        return (
                                            <Tooltip key={hourIdx} title={`Limit: ${limit}`} arrow placement="top">
                                                <Box
                                                    className={MSM.updateHistoryHourBlock}
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: '#ffffff',
                                                        borderRadius: '8px',
                                                        border: '1px solid #e0e0e0',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        overflow: 'hidden',
                                                        '&:hover': {
                                                            backgroundColor: '#f0f0f0',
                                                            transform: 'scale(1.05)',
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            p: 1.5,
                                                            pt: 1,
                                                            pb: 1,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: '12px',
                                                                mb: 0.5,
                                                                fontWeight: 'bold',
                                                            }}
                                                        >
                                                            {`${hourString}:00`}
                                                        </Typography>
                                                    </Box>
                                                    <Divider
                                                        sx={{
                                                            width: '100%',
                                                            borderColor: '#e0e0e0',
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            p: 1.5,
                                                            pt: 1,
                                                            pb: 1,
                                                            backgroundColor: '#f5f5f5',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: '16px',
                                                                color: limit > 0 ? '#000' : '#999',
                                                            }}
                                                        >
                                                            {limit}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Tooltip>
                                        );
                                    })}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </TableContainer>
        </Box>
    );
};
